import { z } from 'zod';
import { ApprovalStatus } from '../contracts/hitl.contract';
import { DataSourceType, DocumentStatus } from '../enums';
import { FileUploadSchema } from '../schemas/file.schemas';
import { DocumentMetadataSchema } from '../schemas/metadata.schemas';
import { ListQuerySchema } from './common.dto';

/**
 * Document DTO Schema
 */
export const DocumentSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  name: z.string().min(1).max(255),
  originalName: z.string(),
  mimeType: z.string(),
  size: z.number().int().positive(), // Size in bytes
  status: z.nativeEnum(DocumentStatus).default(DocumentStatus.PENDING),
  sourceType: z.nativeEnum(DataSourceType).default(DataSourceType.DOCUMENT),
  gcsPath: z.string().optional(), // Path in Google Cloud Storage (admin-only; stripped from non-admin DTOs)
  metadata: DocumentMetadataSchema.optional(),
  chunkCount: z.number().int().nonnegative().default(0),
  uploadedBy: z.string().uuid(),
  processedAt: z.date().nullable().optional(),
  // Approval workflow fields
  approvalStatus: z.nativeEnum(ApprovalStatus).nullable().optional(),
  rejectionReason: z.string().nullable().optional(),
  reviewedBy: z.string().uuid().nullable().optional(),
  reviewedAt: z.date().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type Document = z.infer<typeof DocumentSchema>;

/**
 * Create Document DTO Schema (API Request)
 * - businessId and uploadedBy are set by the server from auth context
 */
export const CreateDocumentRequestSchema = z.object({
  name: z.string().min(1).max(255),
  originalName: z.string(),
  mimeType: z.string(),
  size: z.number().int().positive(),
  gcsPath: z.string(),
  metadata: DocumentMetadataSchema.optional(),
});

export type CreateDocumentRequest = z.infer<typeof CreateDocumentRequestSchema>;

/**
 * Create Document DTO Schema (Internal)
 */
export const CreateDocumentSchema = z.object({
  businessId: z.string().uuid(),
  name: z.string().min(1).max(255),
  originalName: z.string(),
  mimeType: z.string(),
  size: z.number().int().positive(),
  gcsPath: z.string(),
  uploadedBy: z.string().uuid(),
  metadata: DocumentMetadataSchema.optional(),
});

export type CreateDocument = z.infer<typeof CreateDocumentSchema>;

/**
 * Document Chunk DTO Schema (for RAG)
 */
export const DocumentChunkSchema = z.object({
  id: z.string().uuid(),
  documentId: z.string().uuid(),
  businessId: z.string().uuid(),
  content: z.string(),
  embedding: z.array(z.number()).length(1536).optional(), // OpenAI ada-002 dimension
  chunkIndex: z.number().int().nonnegative(),
  metadata: DocumentMetadataSchema.optional(),
  createdAt: z.date(),
});

export type DocumentChunk = z.infer<typeof DocumentChunkSchema>;

/**
 * Upload Document Request DTO Schema
 */
export const UploadDocumentRequestSchema = z.object({
  businessId: z.string().uuid(),
  file: FileUploadSchema,
  metadata: DocumentMetadataSchema.optional(),
  /** When re-uploading a rejected document, the EntityAttachment ID to replace */
  replacesAttachmentId: z.string().uuid().optional(),
});

export type UploadDocumentRequest = z.infer<typeof UploadDocumentRequestSchema>;

/**
 * Document Stats Response DTO Schema
 * For /admin/documents/stats endpoint
 */
export const DocumentStatsSchema = z.object({
  totalDocuments: z.number().int().nonnegative(),
  processedCount: z.number().int().nonnegative(),
  processingCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  pendingCount: z.number().int().nonnegative(),
  totalChunks: z.number().int().nonnegative(),
  totalSize: z.number().int().nonnegative(), // in bytes
});

export type DocumentStats = z.infer<typeof DocumentStatsSchema>;

/**
 * Document List Query Schema (extends ListQuery with status filter)
 * For /admin/documents endpoint
 */
export const DocumentListQuerySchema = ListQuerySchema.extend({
  status: z
    .enum(['pending', 'processing', 'completed', 'failed'])
    .optional()
    .describe('Filter by document processing status'),
  businessId: z.string().uuid().optional().describe('Filter by business ID (super_admin only)'),
});

export type DocumentListQuery = z.infer<typeof DocumentListQuerySchema>;

/**
 * Processing Queue Item Schema
 */
export const ProcessingQueueItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  status: z.string(),
  createdAt: z.date(),
});

export type ProcessingQueueItem = z.infer<typeof ProcessingQueueItemSchema>;

/**
 * Completed Document Item Schema
 */
export const CompletedDocumentItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  processedAt: z.date().nullable(),
});

export type CompletedDocumentItem = z.infer<typeof CompletedDocumentItemSchema>;

/**
 * Failed Document Item Schema
 */
export const FailedDocumentItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  error: z.string(),
});

export type FailedDocumentItem = z.infer<typeof FailedDocumentItemSchema>;

/**
 * Processing Queue Status Response DTO Schema
 * For /admin/documents/processing endpoint
 */
export const ProcessingQueueStatusSchema = z.object({
  queueLength: z.number().int().nonnegative(),
  processing: z.array(ProcessingQueueItemSchema),
  recentlyCompleted: z.array(CompletedDocumentItemSchema),
  recentlyFailed: z.array(FailedDocumentItemSchema),
});

export type ProcessingQueueStatus = z.infer<typeof ProcessingQueueStatusSchema>;

/**
 * Reprocess Document Response DTO Schema
 * For POST /admin/documents/:id/reprocess endpoint
 */
export const ReprocessDocumentResponseSchema = z.object({
  message: z.string(),
  documentId: z.string().uuid(),
  status: z.string(),
});

export type ReprocessDocumentResponse = z.infer<typeof ReprocessDocumentResponseSchema>;

/**
 * Approve Document Request DTO Schema
 * For tRPC documents.approve procedure
 */
export const ApproveDocumentRequestSchema = z.object({
  id: z.string().uuid(),
});

export type ApproveDocumentRequest = z.infer<typeof ApproveDocumentRequestSchema>;

/**
 * Reject Document Request DTO Schema
 * For tRPC documents.reject procedure
 */
export const RejectDocumentRequestSchema = z.object({
  id: z.string().uuid(),
  rejectionReason: z.string().min(1, 'Rejection reason is required').max(1000),
});

export type RejectDocumentRequest = z.infer<typeof RejectDocumentRequestSchema>;

/**
 * Document Download Request DTO Schema
 * For tRPC documents.download procedure
 *
 * Issue #496: Document download functionality
 */
export const DocumentDownloadRequestSchema = z.object({
  id: z.string().uuid(),
});

export type DocumentDownloadRequest = z.infer<typeof DocumentDownloadRequestSchema>;

/**
 * Document Download Response DTO Schema
 * Returns base64-encoded file data for browser download
 *
 * Issue #496: Document download functionality
 */
export const DocumentDownloadResponseSchema = z.object({
  data: z.string(), // Base64-encoded file content
  contentType: z.string(),
  filename: z.string(),
  size: z.number().int().positive(),
});

export type DocumentDownloadResponse = z.infer<typeof DocumentDownloadResponseSchema>;
